// Review sayfası ↔ rapor tutarlılık denetimi yapıştırıcısı: sekme state'i (?tab=consistency),
// store yükleme/temizleme, proje SSE olayları, "alıntıyı bölümde aç" davranışı ve
// kaydedilmemiş değişiklik onayı (başka bölüme geçerken editör taslağı sessizce kaybolmasın).
import { createElement, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import type { ReviewTab } from "../components/review/ReportCheckTabSwitch";
import { REPORT_CHECK_COPY } from "../lib/report-check-copy";
import { locateExcerptInEditor } from "../lib/report-check-locate";
import { useReportCheckStore, type ReportCheckEvent } from "../stores/report-check-store";
import { useProjectEvents } from "./use-project-events";

interface PendingOpen {
  code: string;
  excerpt?: string;
}

export function useReportCheckReview(
  projectId: string,
  setActiveCode: (code: string) => void,
  activeCode: string | null,
  /** Getter — `hasUnsavedChanges` Review'da erken dönüşlerden sonra hesaplanır. */
  hasUnsavedChanges: () => boolean,
): {
  tab: ReviewTab;
  setTab: (t: ReviewTab) => void;
  onOpenSection: (c: string, x?: string) => void;
  onSelectSection: (c: string) => void;
  confirmNode: ReactNode;
} {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<ReviewTab>(
    params.get("tab") === "consistency" ? "consistency" : "sections",
  );
  const [pending, setPending] = useState<PendingOpen | null>(null);
  useEffect(() => {
    void useReportCheckStore.getState().load(projectId);
    return () => useReportCheckStore.getState().reset();
  }, [projectId]);
  // Sekmeye her girişte tazele: bölüm düzenlenip kaydedilince bayatlık bandı güncel olsun.
  // İlk açılışta (derin bağlantı dahil) mount effect'i zaten yükler — çift istek olmasın.
  const prevTab = useRef(tab);
  useEffect(() => {
    if (tab === "consistency" && prevTab.current !== tab) {
      void useReportCheckStore.getState().load(projectId);
    }
    prevTab.current = tab;
  }, [tab, projectId]);
  useProjectEvents(projectId, (evt) => {
    if (evt.type === "report_check.status") {
      useReportCheckStore.getState().applyEvent(evt as ReportCheckEvent);
    }
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const go = useCallback(
    (code: string, excerpt?: string): void => {
      setTab("sections");
      setActiveCode(code);
      // Editör yeni bölümle yeniden çizildikten sonra ilk eşleşmeyi seç/kaydır.
      if (timer.current) clearTimeout(timer.current);
      if (excerpt) timer.current = setTimeout(() => locateExcerptInEditor(excerpt), 150);
    },
    [setActiveCode],
  );
  const onOpenSection = useCallback(
    (code: string, excerpt?: string): void => {
      if (code !== activeCode && hasUnsavedChanges()) {
        setPending(excerpt === undefined ? { code } : { code, excerpt });
        return;
      }
      go(code, excerpt);
    },
    [activeCode, hasUnsavedChanges, go],
  );
  // Sol listeden bölüm seçimi: Tutarlılık sekmesindeyken bölümler görünümüne döner.
  const onSelectSection = useCallback(
    (code: string): void => {
      setTab("sections");
      setActiveCode(code);
    },
    [setActiveCode],
  );
  const c = REPORT_CHECK_COPY.unsavedConfirm;
  const confirmNode = createElement(ConfirmDialog, {
    open: pending !== null,
    title: c.title,
    body: c.body,
    confirmLabel: c.confirmLabel,
    cancelLabel: c.cancelLabel,
    tone: "danger",
    onConfirm: () => {
      const p = pending;
      setPending(null);
      if (p) go(p.code, p.excerpt);
    },
    onCancel: () => setPending(null),
  });
  return { tab, setTab, onOpenSection, onSelectSection, confirmNode };
}
